// src/components/ProtectedRoute/Admin/Dialogs/parts/ScientificNameSelector.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Autocomplete, TextField, Stack, Button, Box, Typography, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DialogScientificNameEditor from "../DialogScientificNameEditor";
import supabase from "../../../../../utils/supabase";
import { useDebounce } from "use-debounce";
import { extractAuthorNames, formatScientificNameLabel } from "../../../../../utils/scientificNameFormatter";

/**
 * 幅いっぱい＆縦配置対応の学名セレクタ
 */
export default function ScientificNameSelector({
  onChange,
  onRefreshData,
  value = "",
  valueId = "",
  isRequired = false,
  disabled = false,

  // UI
  label = "Scientific Name",
  placeholder = "Search by name / id",
  size = "small",
  showInputLabel = false, // true: TextFieldのlabelとして表示
  inlineAdd = true,       // ラベル行右に「Add Name」を表示
  fullWidth = true,       // 幅いっぱいに広げる
  sx = {},                // 追加スタイル（rootに適用）
}) {
  const [selectedId, setSelectedId] = useState(valueId || value || "");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [debounced] = useDebounce(inputValue, 1000);
  const [loading, setLoading] = useState(false);
  const selectedOptionRef = React.useRef(null);
  const RPC_FUZZY = "search_scientific_names_fuzzy";
  const mergePreferRich = (arr, incoming) => {
    const idx = arr.findIndex((x) => x.id === incoming.id);
    if (idx === -1) return [...arr, incoming];
    const cur = arr[idx];
    const prefer = {
      ...cur,
      ...incoming,
      _parent_label: incoming._parent_label || cur._parent_label || "",
      authority_year: incoming.authority_year || cur.authority_year || "",
      name_spell_valid: incoming.name_spell_valid || cur.name_spell_valid,
      name_spell_original: incoming.name_spell_original || cur.name_spell_original,
    };
    const next = [...arr];
    next[idx] = prefer;
    return next;
  };

  const sanitize = (q) =>
    (q || "")
      .replace(/[(),]/g, " ")
      .replace(/[^a-zA-Z0-9_\s-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const fetchOptions = useCallback(
    async (q) => {
      const clean = sanitize(q);
      if (!clean || clean.length < 2) {
        setOptions(selectedOptionRef.current ? [selectedOptionRef.current] : []);
        return;
      }
      const tokens = clean.split(/\s+/).filter(Boolean).map((t) => t.toLowerCase());
      setLoading(true);
      try {
        // まず fuzzy RPC を叩く
        const { data: fuzzy, error: fuzzyErr } = await supabase.rpc(RPC_FUZZY, { q: clean });
        if (!fuzzyErr && Array.isArray(fuzzy) && fuzzy.length) {
          const mappedRaw = fuzzy.map((r) => ({
            ...r,
            _parent_label: r.parent_name || "",
            _authority_authors: extractAuthorNames(r),
          }));
          const containsAll = (row) => {
            const hay = [
              row.name_spell_valid,
              row.name_spell_original,
              row.authority_year,
              row._parent_label,
              row.authors_str,
              Array.isArray(row._authority_authors) ? row._authority_authors.join(" ") : "",
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return tokens.every((t) => hay.includes(t));
          };
          const filtered = tokens.length > 1 ? mappedRaw.filter(containsAll) : mappedRaw;
          setOptions(filtered.length ? filtered : mappedRaw);
          setLoading(false);
          return;
        }

        // フォールバック: 既存 ilike 方式
        const likeTarget = tokens[0] || clean;
        const like = `%${likeTarget}%`;
        const { data, error } = await supabase
          .from("scientific_names")
          .select(`id, name_spell_valid, name_spell_original, authority_year, current_rank, current_parent,
                   scientific_name_and_author!left(author_order, researcher:researcher_id(last_name, first_name))`)
          .or(`id.ilike.${like},name_spell_valid.ilike.${like},name_spell_original.ilike.${like}`)
          .order("name_spell_valid", { ascending: true })
          .limit(40);
        if (!error) {
          let base = (data || []).map((r) => ({
            ...r,
            _authority_authors: extractAuthorNames(r),
          }));
          const parentIds = Array.from(new Set(base.map((r) => r.current_parent).filter(Boolean)));
          if (parentIds.length) {
            try {
              const { data: parents } = await supabase
                .from("scientific_names")
                .select("id, name_spell_valid")
                .in("id", parentIds);
              const pMap = new Map(
                (parents || []).map((p) => [
                  p.id,
                  `${p.name_spell_valid || p.id}`,
                ])
              );
              base = base.map((r) => ({
                ...r,
                _parent_label: pMap.get(r.current_parent) || r.current_parent,
              }));
            } catch (e) {
              console.warn("[ScientificNameSelector] parent resolve failed", e);
            }
          }
          const containsAll = (row) => {
            const hay = [
              row.name_spell_valid,
              row.name_spell_original,
              row.authority_year,
              row._parent_label,
              Array.isArray(row._authority_authors) ? row._authority_authors.join(" ") : "",
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
          return tokens.every((t) => hay.includes(t));
          };
          const containsAny = (row) => {
            if (!tokens.length) return true;
            const hay = [
              row.name_spell_valid,
              row.name_spell_original,
              row.authority_year,
              row._parent_label,
              Array.isArray(row._authority_authors) ? row._authority_authors.join(" ") : "",
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return tokens.some((t) => hay.includes(t));
          };
          const merged = selectedOptionRef.current
            ? [selectedOptionRef.current, ...base].reduce((arr, item) => {
                if (!item) return arr;
                return mergePreferRich(arr, item);
              }, [])
            : base;
          let filtered = tokens.length > 1 ? merged.filter(containsAll) : merged;
          if (!filtered.length && merged.length) filtered = merged;
          setOptions(filtered);
        }
      } finally {
        setLoading(false);
      }
    },
    [RPC_FUZZY]
  );
  const runSearch = useCallback((q) => {
    fetchOptions(q);
  }, [fetchOptions]);

  useEffect(() => {
    if (debounced && debounced.length >= 2) {
      fetchOptions(debounced);
    } else {
      setOptions((prev) => {
        const sel = selectedOptionRef.current;
        if (!sel) return [];
        return prev.some((o) => o.id === sel.id) ? prev : [sel, ...prev];
      });
    }
  }, [debounced, fetchOptions]);

  // 外部から value / valueId が変わったときに state を同期
  useEffect(() => {
    const next = valueId || value || "";
    setSelectedId(next);
  }, [valueId, value]);

  // selectedId はあるが options / ref に無い場合、単発 fetch で補う
  useEffect(() => {
    const hasOption = options.some((o) => o.id === selectedId);
    if (!selectedId || hasOption || selectedOptionRef.current?.id === selectedId) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("scientific_names")
          .select(`id, name_spell_valid, name_spell_original, authority_year, current_rank, current_parent,
                   scientific_name_and_author!left(author_order, researcher:researcher_id(last_name, first_name))`)
          .eq("id", selectedId)
          .maybeSingle();
        if (error || !data) return;
        let withParent = {
          ...data,
          _authority_authors: Array.isArray(data.scientific_name_and_author)
            ? data.scientific_name_and_author
                .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0))
                .map((x) => [x.researcher?.last_name, x.researcher?.first_name].filter(Boolean).join(", "))
                .filter(Boolean)
            : [],
        };
        if (data.current_parent) {
          const { data: p } = await supabase
            .from("scientific_names")
            .select("id, name_spell_valid")
            .eq("id", data.current_parent)
            .maybeSingle();
          if (p) {
            withParent._parent_label = `${p.name_spell_valid || p.id}`;
          }
        }
        selectedOptionRef.current = withParent;
        setOptions((prev) => (prev.some((o) => o.id === withParent.id) ? prev : [withParent, ...prev]));
      } catch (e) {
        console.warn("[ScientificNameSelector] fetch by id failed", e);
      }
    })();
  }, [selectedId, options]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === selectedId) || selectedOptionRef.current || null,
    [options, selectedId]
  );

  // keep the selected option even when options are cleared
  useEffect(() => {
    if (selectedOption) {
      selectedOptionRef.current = selectedOption;
      // ensure options contains the selected one
      setOptions((prev) => {
        const exists = prev.some((o) => o.id === selectedOption.id);
        return exists ? prev : [selectedOption, ...prev];
      });
    }
  }, [selectedOption]);

  const openAddModal  = () => setIsAddOpen(true);
  const closeAddModal = async (didAdd) => {
    setIsAddOpen(false);
    if (didAdd && typeof onRefreshData === "function") await onRefreshData();
  };

  const formatLabel = useCallback((opt) => {
    if (!opt) return "";
    return formatScientificNameLabel(opt);
  }, []);

  const getLabelString = useCallback((opt) => formatLabel(opt), [formatLabel]);

  const renderOptionRow = useCallback((opt) => {
    const parentName = opt._parent_label || opt.current_parent || "";
    return (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {opt.current_rank}: {parentName}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {opt.name_spell_valid || opt.name_spell_original || opt.id}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {(() => {
            const ay = opt.authority_year || "";
            const auArr = Array.isArray(opt._authority_authors) ? opt._authority_authors : [];
            const parts = [];
            if (auArr.length) parts.push(auArr.join(", "));
            if (ay) parts.push(ay);
            return parts.length ? `[${parts.join(", ")}]` : "";
          })()}
        </Typography>
      </Box>
    );
  }, []);

  return (
    <Box sx={{ width: 1, display: "block", ...sx }}>
      {/* ラベル行（右に Add Name） */}
      {inlineAdd || !showInputLabel ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">
            {label}{isRequired ? " *" : ""}
          </Typography>
          <Button size="small" onClick={openAddModal} startIcon={<AddIcon />} sx={{ minWidth: 0, px: 1 }}>
            {/* 先頭の「+」は付けない → 重複回避 */}
            Add Name
          </Button>
        </Stack>
      ) : null}

      <Autocomplete
        options={options}
        getOptionLabel={getLabelString}
        filterOptions={(opts) => opts} // サーバー結果をそのまま表示
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        value={selectedOption}
        onChange={(e, val) => {
          const id = val?.id || "";
          setSelectedId(id);
          onChange?.(id);
          if (val) selectedOptionRef.current = val;
          // 選択確定時に入力欄をラベルで置き換える
          setInputValue(val ? getLabelString(val) : "");
          if (val) {
            setOptions((prev) => {
              if (prev.some((o) => o.id === val.id)) return prev;
              return [val, ...prev];
            });
          }
        }}
        onInputChange={(_, v, reason) => {
          if (reason === "input") {
            setInputValue(v || "");
          } else if (reason === "clear") {
            setInputValue("");
            // クリア時も選択済みを維持しないためオプションを空に
            setOptions(selectedOptionRef.current ? [selectedOptionRef.current] : []);
          }
          // 検索はユーザー入力のみに限定
          if (reason === "input") runSearch(v || "");
        }}
        size={size}
        disableClearable={!!isRequired}
        disabled={disabled}
        sx={{ width: 1 }}          // ← ルートを強制的に幅100%
        renderOption={(props, option) => {
          // li に余計な属性(button等)が入って警告になるのを防ぐ
          const { key, button, ...liProps } = props;
          return (
            <li {...liProps} key={key}>
              {renderOptionRow(option)}
            </li>
          );
        }}
        ListboxProps={{ sx: { maxHeight: 320, overflowY: "auto" } }}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            placeholder={!showInputLabel ? placeholder : undefined}
            label={showInputLabel ? (isRequired ? `${label} *` : label) : undefined}
            required={!!isRequired}
            error={!!(isRequired && !selectedId)}
            helperText={isRequired && !selectedId ? "This field is required." : ""}
            fullWidth
            margin="dense"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {!inlineAdd && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={openAddModal}>
            Add Name
          </Button>
        </Stack>
      )}

      {isAddOpen && (
        <DialogScientificNameEditor open mode="add" onClose={(didAdd) => closeAddModal(didAdd)} />
      )}
    </Box>
  );
}
